/**
 * Screen detection utility for physically accurate ruler measurements.
 *
 * Core formula:
 *   cssPixelsPerInch = sqrt(screen.width² + screen.height²) / diagonalInches
 *
 * Zoom compensation:
 *   adjustedPxPerInch = cssPixelsPerInch / (currentDPR / baseDPR)
 *
 * Detection chain (priority order):
 *   1. getScreenDetails() monitor label → monitor database
 *   2. screen dimensions + DPR → mobile device database
 *   3. Saved calibration from localStorage
 *   4. User-assisted calibration (reference object / screen size input)
 *   5. Default fallback
 */

// ═══════════════════════════════════════════════════════════════
// Calibration source & confidence
// ═══════════════════════════════════════════════════════════════

export type CalibrationSource =
  | 'device-db'             // ★★★★★ exact mobile device match
  | 'screen-panel'          // ★★★★★ screen panel fingerprint match
  | 'monitor-db'            // ★★★★☆ monitor label match
  | 'calibration-verified'  // ★★★★☆ reference object + user verified
  | 'calibration'           // ★★★☆☆ reference object calibration (unverified)
  | 'screen-size'           // ★★☆☆☆ user selected common screen size
  | 'default';              // ★☆☆☆☆ default fallback

export function getConfidenceStars(source: CalibrationSource): number {
  switch (source) {
    case 'device-db': return 5;
    case 'screen-panel': return 5;
    case 'monitor-db': return 4;
    case 'calibration-verified': return 4;
    case 'calibration': return 3;
    case 'screen-size': return 2;
    case 'default': return 1;
  }
}

export function getConfidenceLabel(source: CalibrationSource, locale: string): string {
  const labels: Record<string, Record<CalibrationSource, string>> = {
    en: {
      'device-db': 'Exact device match',
      'screen-panel': 'Screen panel identified',
      'monitor-db': 'Monitor identified',
      'calibration-verified': 'Calibrated & verified',
      'calibration': 'Calibrated',
      'screen-size': 'Estimated from screen size',
      'default': 'Default (uncalibrated)',
    },
    zh: {
      'device-db': '精确设备匹配',
      'screen-panel': '屏幕面板已识别',
      'monitor-db': '已识别显示器',
      'calibration-verified': '已校准并验证',
      'calibration': '已校准',
      'screen-size': '根据屏幕尺寸估算',
      'default': '默认值（未校准）',
    },
    nl: {
      'device-db': 'Exact apparaat herkend',
      'screen-panel': 'Schermpaneel herkend',
      'monitor-db': 'Monitor herkend',
      'calibration-verified': 'Gekalibreerd & geverifieerd',
      'calibration': 'Gekalibreerd',
      'screen-size': 'Geschat op basis van schermgrootte',
      'default': 'Standaard (niet gekalibreerd)',
    },
  };
  return (labels[locale] || labels.en)[source];
}

// ═══════════════════════════════════════════════════════════════
// Mobile device database
// ═══════════════════════════════════════════════════════════════

export interface DeviceSpecs {
  screenWidth: number;   // CSS pixels
  screenHeight: number;  // CSS pixels
  baseDPR: number;
  diagonalInches: number;
  name: string;
}

const knownDevices: DeviceSpecs[] = [
  // ── iPhone ──
  { screenWidth: 440, screenHeight: 956, baseDPR: 3, diagonalInches: 6.9, name: 'iPhone 16 Pro Max' },
  { screenWidth: 402, screenHeight: 874, baseDPR: 3, diagonalInches: 6.3, name: 'iPhone 16 Pro' },
  { screenWidth: 430, screenHeight: 932, baseDPR: 3, diagonalInches: 6.7, name: 'iPhone 16 Plus / 15 Pro Max / 15 Plus / 14 Pro Max' },
  { screenWidth: 393, screenHeight: 852, baseDPR: 3, diagonalInches: 6.1, name: 'iPhone 16 / 15 Pro / 15' },
  { screenWidth: 428, screenHeight: 926, baseDPR: 3, diagonalInches: 6.7, name: 'iPhone 14 Plus / 13 Pro Max / 12 Pro Max' },
  { screenWidth: 390, screenHeight: 844, baseDPR: 3, diagonalInches: 6.1, name: 'iPhone 14 / 13 Pro / 13 / 12 Pro / 12' },
  { screenWidth: 375, screenHeight: 812, baseDPR: 3, diagonalInches: 5.8, name: 'iPhone 13 mini / 12 mini / X / XS / 11 Pro' },
  { screenWidth: 414, screenHeight: 896, baseDPR: 3, diagonalInches: 6.5, name: 'iPhone 11 Pro Max / XS Max' },
  { screenWidth: 414, screenHeight: 896, baseDPR: 2, diagonalInches: 6.1, name: 'iPhone 11 / XR' },
  { screenWidth: 414, screenHeight: 736, baseDPR: 3, diagonalInches: 5.5, name: 'iPhone 8 Plus / 7 Plus / 6s Plus' },
  { screenWidth: 375, screenHeight: 667, baseDPR: 2, diagonalInches: 4.7, name: 'iPhone SE (2/3) / 8 / 7 / 6s' },
  { screenWidth: 320, screenHeight: 568, baseDPR: 2, diagonalInches: 4.0, name: 'iPhone SE (1st) / 5s' },

  // ── iPad ──
  { screenWidth: 1032, screenHeight: 1376, baseDPR: 2, diagonalInches: 13, name: 'iPad Pro 13" M4' },
  { screenWidth: 1024, screenHeight: 1366, baseDPR: 2, diagonalInches: 12.9, name: 'iPad Pro 12.9"' },
  { screenWidth: 834, screenHeight: 1210, baseDPR: 2, diagonalInches: 11, name: 'iPad Pro 11" M4 / iPad Air 13"' },
  { screenWidth: 834, screenHeight: 1194, baseDPR: 2, diagonalInches: 11, name: 'iPad Pro 11"' },
  { screenWidth: 820, screenHeight: 1180, baseDPR: 2, diagonalInches: 10.9, name: 'iPad Air (5th) / iPad (10th)' },
  { screenWidth: 810, screenHeight: 1080, baseDPR: 2, diagonalInches: 10.2, name: 'iPad (9th)' },
  { screenWidth: 744, screenHeight: 1133, baseDPR: 2, diagonalInches: 8.3, name: 'iPad mini (6th/7th)' },

  // ── Samsung Galaxy S ──
  { screenWidth: 384, screenHeight: 824, baseDPR: 2.8125, diagonalInches: 6.8, name: 'Galaxy S24 Ultra / S23 Ultra' },
  { screenWidth: 384, screenHeight: 854, baseDPR: 3, diagonalInches: 6.7, name: 'Galaxy S24+' },
  { screenWidth: 360, screenHeight: 780, baseDPR: 3, diagonalInches: 6.2, name: 'Galaxy S24 / S23' },
  { screenWidth: 384, screenHeight: 854, baseDPR: 2.8125, diagonalInches: 6.6, name: 'Galaxy S23+ / S22+' },
  { screenWidth: 360, screenHeight: 780, baseDPR: 3, diagonalInches: 6.1, name: 'Galaxy S22' },
  { screenWidth: 384, screenHeight: 832, baseDPR: 2.8125, diagonalInches: 6.9, name: 'Galaxy S25 Ultra' },
  { screenWidth: 384, screenHeight: 854, baseDPR: 3, diagonalInches: 6.7, name: 'Galaxy S25+' },
  { screenWidth: 360, screenHeight: 780, baseDPR: 3, diagonalInches: 6.2, name: 'Galaxy S25' },

  // ── Samsung Galaxy Z ──
  { screenWidth: 344, screenHeight: 882, baseDPR: 3, diagonalInches: 7.6, name: 'Galaxy Z Fold5 (inner)' },
  { screenWidth: 412, screenHeight: 916, baseDPR: 2.625, diagonalInches: 6.7, name: 'Galaxy Z Flip5' },
  { screenWidth: 360, screenHeight: 906, baseDPR: 3, diagonalInches: 7.6, name: 'Galaxy Z Fold6 (inner)' },
  { screenWidth: 412, screenHeight: 932, baseDPR: 2.625, diagonalInches: 6.7, name: 'Galaxy Z Flip6' },

  // ── Samsung Tab ──
  { screenWidth: 753, screenHeight: 1205, baseDPR: 2, diagonalInches: 14.6, name: 'Galaxy Tab S9 Ultra' },
  { screenWidth: 753, screenHeight: 1133, baseDPR: 2, diagonalInches: 12.4, name: 'Galaxy Tab S9+' },
  { screenWidth: 753, screenHeight: 1005, baseDPR: 2, diagonalInches: 11, name: 'Galaxy Tab S9' },

  // ── Xiaomi ──
  { screenWidth: 393, screenHeight: 873, baseDPR: 2.75, diagonalInches: 6.73, name: 'Xiaomi 14 Ultra / 14 Pro / 13 Ultra / 13 Pro' },
  { screenWidth: 393, screenHeight: 851, baseDPR: 2.75, diagonalInches: 6.36, name: 'Xiaomi 14 / 13' },
  { screenWidth: 393, screenHeight: 873, baseDPR: 2.75, diagonalInches: 6.67, name: 'Redmi Note 13 Pro+' },
  { screenWidth: 412, screenHeight: 915, baseDPR: 2.625, diagonalInches: 6.78, name: 'Xiaomi 15 Pro' },
  { screenWidth: 393, screenHeight: 873, baseDPR: 2.75, diagonalInches: 6.36, name: 'Xiaomi 15' },

  // ── Huawei ──
  { screenWidth: 393, screenHeight: 873, baseDPR: 3, diagonalInches: 6.82, name: 'Huawei Mate 60 Pro+ / Mate 60 Pro' },
  { screenWidth: 393, screenHeight: 851, baseDPR: 3, diagonalInches: 6.69, name: 'Huawei Mate 60' },
  { screenWidth: 393, screenHeight: 873, baseDPR: 3, diagonalInches: 6.67, name: 'Huawei P60 Pro / P60' },
  { screenWidth: 400, screenHeight: 889, baseDPR: 3, diagonalInches: 6.82, name: 'Huawei Mate 70 Pro' },

  // ── Google Pixel ──
  { screenWidth: 412, screenHeight: 915, baseDPR: 2.625, diagonalInches: 6.7, name: 'Pixel 8 Pro / 7 Pro' },
  { screenWidth: 412, screenHeight: 892, baseDPR: 2.625, diagonalInches: 6.2, name: 'Pixel 8 / 7' },
  { screenWidth: 412, screenHeight: 823, baseDPR: 2.625, diagonalInches: 6.1, name: 'Pixel 6' },
  { screenWidth: 412, screenHeight: 932, baseDPR: 2.625, diagonalInches: 6.7, name: 'Pixel 9 Pro' },
  { screenWidth: 412, screenHeight: 892, baseDPR: 2.625, diagonalInches: 6.3, name: 'Pixel 9' },

  // ── OPPO / OnePlus ──
  { screenWidth: 412, screenHeight: 915, baseDPR: 2.625, diagonalInches: 6.82, name: 'OnePlus 12' },
  { screenWidth: 393, screenHeight: 873, baseDPR: 2.75, diagonalInches: 6.7, name: 'OPPO Find X7 Ultra' },
];

/**
 * Try to auto-detect the current device from the known database.
 * Matches screen CSS dimensions + DPR (with tolerance).
 */
export function autoDetectDevice(): DeviceSpecs | null {
  const sw = Math.min(window.screen.width, window.screen.height);
  const sh = Math.max(window.screen.width, window.screen.height);
  const dpr = window.devicePixelRatio;

  for (const device of knownDevices) {
    const dw = Math.min(device.screenWidth, device.screenHeight);
    const dh = Math.max(device.screenWidth, device.screenHeight);

    if (sw === dw && sh === dh && Math.abs(dpr - device.baseDPR) < 0.2) {
      return device;
    }
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════
// Desktop monitor database (for getScreenDetails() API)
// ═══════════════════════════════════════════════════════════════

export interface MonitorSpecs {
  pattern: RegExp;        // Match against screen label
  diagonalInches: number;
  physicalWidthMM: number;
  physicalHeightMM: number;
  name: string;
}

/**
 * Database of common desktop monitors.
 * The `pattern` field matches the label string returned by getScreenDetails().
 * Labels typically contain manufacturer + model (from EDID).
 */
const knownMonitors: MonitorSpecs[] = [
  // ── Apple ──
  { pattern: /built.in.*retina/i, diagonalInches: 0, physicalWidthMM: 0, physicalHeightMM: 0, name: 'MacBook (Retina)' },
  // Note: Built-in Retina displays need screen dimensions to determine exact model, handled separately
  { pattern: /studio display/i, diagonalInches: 27, physicalWidthMM: 596, physicalHeightMM: 335, name: 'Apple Studio Display' },
  { pattern: /pro display xdr/i, diagonalInches: 32, physicalWidthMM: 709, physicalHeightMM: 399, name: 'Apple Pro Display XDR' },

  // ── Dell ──
  { pattern: /dell.*u2720/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Dell U2720Q' },
  { pattern: /dell.*u2723/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Dell U2723QE' },
  { pattern: /dell.*u2724/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Dell U2724D' },
  { pattern: /dell.*u3223/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'Dell U3223QE' },
  { pattern: /dell.*u3423/i, diagonalInches: 34, physicalWidthMM: 797, physicalHeightMM: 334, name: 'Dell U3423WE' },
  { pattern: /dell.*u2422/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'Dell U2422H' },
  { pattern: /dell.*p2422/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'Dell P2422H' },
  { pattern: /dell.*p2723/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Dell P2723QE' },
  { pattern: /dell.*s2722/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Dell S2722QC' },
  { pattern: /dell.*s2721/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Dell S2721DGF' },
  { pattern: /dell.*s2421/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'Dell S2421HGF' },
  { pattern: /dell.*aw2723/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Dell AW2723DF' },
  { pattern: /dell.*aw3423/i, diagonalInches: 34, physicalWidthMM: 797, physicalHeightMM: 334, name: 'Dell AW3423DW' },

  // ── LG ──
  { pattern: /lg.*27u[kln]/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'LG 27UK/UL/UN' },
  { pattern: /lg.*27g[pn]/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'LG 27GP/GN' },
  { pattern: /lg.*32u[kln]/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'LG 32UK/UL/UN' },
  { pattern: /lg.*34w[kn]/i, diagonalInches: 34, physicalWidthMM: 797, physicalHeightMM: 334, name: 'LG 34WK/WN' },
  { pattern: /lg.*27m[dp]/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'LG 27MD/MP' },
  { pattern: /lg.*24m[dp]/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'LG 24MD/MP' },
  { pattern: /lg\s*ultragear.*27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'LG UltraGear 27"' },
  { pattern: /lg\s*ultragear.*32/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'LG UltraGear 32"' },
  { pattern: /lg\s*ultrafine.*27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'LG UltraFine 27"' },
  { pattern: /lg\s*ultrafine.*24/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'LG UltraFine 24"' },
  { pattern: /lg\s*ultrawide.*34/i, diagonalInches: 34, physicalWidthMM: 797, physicalHeightMM: 334, name: 'LG UltraWide 34"' },

  // ── Samsung ──
  { pattern: /samsung.*ls27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Samsung 27"' },
  { pattern: /samsung.*ls32/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'Samsung 32"' },
  { pattern: /samsung.*ls24/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'Samsung 24"' },
  { pattern: /samsung.*ls34/i, diagonalInches: 34, physicalWidthMM: 797, physicalHeightMM: 334, name: 'Samsung 34"' },
  { pattern: /samsung.*odyssey.*g7.*27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Samsung Odyssey G7 27"' },
  { pattern: /samsung.*odyssey.*g7.*32/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'Samsung Odyssey G7 32"' },
  { pattern: /samsung.*smart\s*monitor.*27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Samsung Smart Monitor 27"' },
  { pattern: /samsung.*smart\s*monitor.*32/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'Samsung Smart Monitor 32"' },

  // ── ASUS ──
  { pattern: /asus.*pa278/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'ASUS PA278QV' },
  { pattern: /asus.*vg27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'ASUS VG27' },
  { pattern: /asus.*pg27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'ASUS ROG PG27' },
  { pattern: /asus.*xg27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'ASUS ROG XG27' },
  { pattern: /asus.*vg24/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'ASUS VG24' },
  { pattern: /asus.*pg32/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'ASUS ROG PG32' },

  // ── BenQ ──
  { pattern: /benq.*pd27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'BenQ PD27' },
  { pattern: /benq.*pd32/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'BenQ PD32' },
  { pattern: /benq.*ew27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'BenQ EW27' },
  { pattern: /benq.*xl24/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'BenQ XL24' },

  // ── HP ──
  { pattern: /hp.*z27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'HP Z27' },
  { pattern: /hp.*z24/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'HP Z24' },
  { pattern: /hp.*z32/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'HP Z32' },
  { pattern: /hp.*e27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'HP E27' },
  { pattern: /hp.*e24/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'HP E24' },

  // ── AOC ──
  { pattern: /aoc.*27g/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'AOC 27G' },
  { pattern: /aoc.*24g/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'AOC 24G' },
  { pattern: /aoc.*u27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'AOC U27' },

  // ── Lenovo ──
  { pattern: /lenovo.*t27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Lenovo ThinkVision T27' },
  { pattern: /lenovo.*t24/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'Lenovo ThinkVision T24' },
  { pattern: /lenovo.*p27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Lenovo ThinkVision P27' },
  { pattern: /lenovo.*q27/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Lenovo Q27' },

  // ── Philips ──
  { pattern: /philips.*27[6-9]/i, diagonalInches: 27, physicalWidthMM: 597, physicalHeightMM: 336, name: 'Philips 27"' },
  { pattern: /philips.*24[5-9]/i, diagonalInches: 24, physicalWidthMM: 527, physicalHeightMM: 296, name: 'Philips 24"' },
  { pattern: /philips.*32[5-9]/i, diagonalInches: 32, physicalWidthMM: 698, physicalHeightMM: 393, name: 'Philips 32"' },

  // ── Generic size extraction fallback ──
  // These catch monitors where we can extract the size from the label number
  // e.g. "Some Monitor 27Q" → 27 inches
];

/**
 * MacBook models identified by screen.width/height when label says "Built-in Retina"
 */
interface MacBookModel {
  cssWidth: number;
  cssHeight: number;
  diagonalInches: number;
  physicalWidthMM: number;
  physicalHeightMM: number;
  name: string;
}

const macBookModels: MacBookModel[] = [
  { cssWidth: 1512, cssHeight: 982, diagonalInches: 14.2, physicalWidthMM: 312, physicalHeightMM: 202, name: 'MacBook Pro 14"' },
  { cssWidth: 1728, cssHeight: 1117, diagonalInches: 16.2, physicalWidthMM: 356, physicalHeightMM: 231, name: 'MacBook Pro 16"' },
  { cssWidth: 1470, cssHeight: 956, diagonalInches: 15.3, physicalWidthMM: 338, physicalHeightMM: 220, name: 'MacBook Air 15"' },
  { cssWidth: 1440, cssHeight: 900, diagonalInches: 13.3, physicalWidthMM: 286, physicalHeightMM: 179, name: 'MacBook Air 13" (Intel)' },
  { cssWidth: 1280, cssHeight: 800, diagonalInches: 13.3, physicalWidthMM: 286, physicalHeightMM: 179, name: 'MacBook Pro 13"' },
  { cssWidth: 1440, cssHeight: 932, diagonalInches: 13.6, physicalWidthMM: 296, physicalHeightMM: 192, name: 'MacBook Air 13" M2/M3/M4' },
];

// ═══════════════════════════════════════════════════════════════
// Screen panel fingerprint database (auto-detect by native resolution)
// ═══════════════════════════════════════════════════════════════

/**
 * Screen panels identified by native resolution (screen.width * DPR × screen.height * DPR).
 * This provides a platform-independent way to identify screens without getScreenDetails() API.
 *
 * Key insight: native resolution rarely changes even when users change display scaling.
 * On macOS with non-default "Looks like" scaling, screen.width * DPR ≠ true native res,
 * but the ASPECT RATIO of (screen.width, screen.height) matches the panel's native ratio.
 * So we use two matching strategies:
 *   1. Exact native resolution match (works for default scaling + mobile + Windows)
 *   2. Aspect ratio match within a platform (fallback for macOS scaled resolutions)
 */
interface ScreenPanel {
  nativeWidth: number;     // Physical pixel width
  nativeHeight: number;    // Physical pixel height
  diagonalInches: number;
  name: string;
  platform?: 'mac' | 'windows' | 'chromeos';  // optional platform filter
}

const knownScreenPanels: ScreenPanel[] = [
  // ── Apple MacBook (native resolutions, DPR always 2) ──
  { nativeWidth: 3024, nativeHeight: 1964, diagonalInches: 14.2, name: 'MacBook Pro 14"', platform: 'mac' },
  { nativeWidth: 3456, nativeHeight: 2234, diagonalInches: 16.2, name: 'MacBook Pro 16"', platform: 'mac' },
  { nativeWidth: 2560, nativeHeight: 1664, diagonalInches: 13.6, name: 'MacBook Air 13" (M2+)', platform: 'mac' },
  { nativeWidth: 2880, nativeHeight: 1864, diagonalInches: 15.3, name: 'MacBook Air 15"', platform: 'mac' },
  { nativeWidth: 2560, nativeHeight: 1600, diagonalInches: 13.3, name: 'MacBook Pro 13" / Air (Intel)', platform: 'mac' },
  { nativeWidth: 2304, nativeHeight: 1440, diagonalInches: 12, name: 'MacBook 12"', platform: 'mac' },
  // Apple iMac / displays
  { nativeWidth: 4480, nativeHeight: 2520, diagonalInches: 23.5, name: 'iMac 24" (4.5K)', platform: 'mac' },
  { nativeWidth: 5120, nativeHeight: 2880, diagonalInches: 27, name: 'iMac 27" 5K / Studio Display', platform: 'mac' },
  { nativeWidth: 6016, nativeHeight: 3384, diagonalInches: 32, name: 'Pro Display XDR', platform: 'mac' },

  // ── Microsoft Surface ──
  { nativeWidth: 2736, nativeHeight: 1824, diagonalInches: 12.3, name: 'Surface Pro 5/6/7', platform: 'windows' },
  { nativeWidth: 2880, nativeHeight: 1920, diagonalInches: 13, name: 'Surface Pro 8/9/10', platform: 'windows' },
  { nativeWidth: 2496, nativeHeight: 1664, diagonalInches: 13, name: 'Surface Pro (11th)', platform: 'windows' },
  { nativeWidth: 2256, nativeHeight: 1504, diagonalInches: 13.5, name: 'Surface Laptop 3/4 13.5"', platform: 'windows' },
  { nativeWidth: 2496, nativeHeight: 1664, diagonalInches: 13.8, name: 'Surface Laptop 5/6 13.5"', platform: 'windows' },
  { nativeWidth: 3000, nativeHeight: 2000, diagonalInches: 15, name: 'Surface Laptop 3/4 15"', platform: 'windows' },
  { nativeWidth: 2304, nativeHeight: 1536, diagonalInches: 12.4, name: 'Surface Pro X', platform: 'windows' },
  { nativeWidth: 3120, nativeHeight: 2080, diagonalInches: 14.4, name: 'Surface Laptop Studio', platform: 'windows' },

  // ── Lenovo ThinkPad (common high-res panels) ──
  { nativeWidth: 2560, nativeHeight: 1600, diagonalInches: 14, name: 'ThinkPad X1 Carbon (2.5K)', platform: 'windows' },
  { nativeWidth: 2880, nativeHeight: 1800, diagonalInches: 14, name: 'ThinkPad X1 Carbon (2.8K)', platform: 'windows' },
  { nativeWidth: 3840, nativeHeight: 2400, diagonalInches: 14, name: 'ThinkPad X1 Carbon (4K)', platform: 'windows' },
  { nativeWidth: 2160, nativeHeight: 1350, diagonalInches: 14, name: 'ThinkPad T14s (2.2K)', platform: 'windows' },

  // ── Dell XPS ──
  { nativeWidth: 1920, nativeHeight: 1200, diagonalInches: 13.4, name: 'Dell XPS 13 (FHD+)', platform: 'windows' },
  { nativeWidth: 2560, nativeHeight: 1600, diagonalInches: 13.4, name: 'Dell XPS 13 (QHD+)', platform: 'windows' },
  { nativeWidth: 3456, nativeHeight: 2160, diagonalInches: 13.4, name: 'Dell XPS 13 (3.5K OLED)', platform: 'windows' },
  { nativeWidth: 3840, nativeHeight: 2400, diagonalInches: 15.6, name: 'Dell XPS 15 (4K)', platform: 'windows' },
  { nativeWidth: 3456, nativeHeight: 2160, diagonalInches: 15.6, name: 'Dell XPS 15 (3.5K OLED)', platform: 'windows' },

  // ── Huawei MateBook ──
  { nativeWidth: 2520, nativeHeight: 1680, diagonalInches: 14.2, name: 'Huawei MateBook X Pro', platform: 'windows' },
  { nativeWidth: 3120, nativeHeight: 2080, diagonalInches: 14.2, name: 'Huawei MateBook X Pro (3.1K)', platform: 'windows' },

  // ── Common laptop panels (generic, no platform filter) ──
  // NOTE: 1920x1080 is too common across many sizes, excluded intentionally
  { nativeWidth: 2560, nativeHeight: 1440, diagonalInches: 14, name: '14" QHD Laptop' },
  { nativeWidth: 2240, nativeHeight: 1400, diagonalInches: 14, name: '14" 2.2K Laptop' },
  { nativeWidth: 2880, nativeHeight: 1800, diagonalInches: 16, name: '16" 2.8K Laptop' },

  // ── Chromebook ──
  { nativeWidth: 2560, nativeHeight: 1600, diagonalInches: 13.3, name: 'Chromebook Pixel / Pixelbook', platform: 'chromeos' },
  { nativeWidth: 2400, nativeHeight: 1600, diagonalInches: 13.5, name: 'Chromebook Pixel Slate', platform: 'chromeos' },
];

/**
 * Detect the current platform from user agent.
 */
function detectPlatform(): 'mac' | 'windows' | 'chromeos' | 'unknown' {
  const ua = navigator.userAgent.toLowerCase();
  const platform = ((navigator as any).platform || '').toLowerCase();
  if (platform.includes('mac') || ua.includes('macintosh')) return 'mac';
  if (ua.includes('cros')) return 'chromeos';
  if (platform.includes('win') || ua.includes('windows')) return 'windows';
  return 'unknown';
}

/**
 * Auto-detect screen by matching native resolution against known panels.
 * Works without any browser permission or user interaction.
 *
 * Strategy 1: Exact native resolution match
 * Strategy 2: Aspect ratio match (for macOS scaled resolutions)
 */
export function autoDetectByScreenPanel(): {
  panel: ScreenPanel;
  diagonalInches: number;
  cssPPI: number;
} | null {
  const sw = window.screen.width;
  const sh = window.screen.height;
  const dpr = window.devicePixelRatio;
  const nw = Math.round(sw * dpr);
  const nh = Math.round(sh * dpr);
  const plat = detectPlatform();

  // Strategy 1: Exact native resolution match
  for (const panel of knownScreenPanels) {
    if (panel.platform && panel.platform !== plat) continue;
    if (nw === panel.nativeWidth && nh === panel.nativeHeight) {
      const cssDiag = Math.sqrt(sw * sw + sh * sh);
      return { panel, diagonalInches: panel.diagonalInches, cssPPI: cssDiag / panel.diagonalInches };
    }
  }

  // Strategy 2 (macOS only): Aspect ratio match
  // On macOS, when using scaled resolutions, nw×nh ≠ native res, but aspect ratio is preserved
  if (plat === 'mac') {
    const ratio = sw / sh;
    const macPanels = knownScreenPanels.filter(p => !p.platform || p.platform === 'mac');
    let bestMatch: ScreenPanel | null = null;
    let bestDiff = Infinity;

    for (const panel of macPanels) {
      const panelRatio = panel.nativeWidth / panel.nativeHeight;
      const diff = Math.abs(ratio - panelRatio);
      if (diff < bestDiff && diff < 0.002) { // tolerance: 0.2%
        bestDiff = diff;
        bestMatch = panel;
      }
    }

    if (bestMatch) {
      const cssDiag = Math.sqrt(sw * sw + sh * sh);
      return { panel: bestMatch, diagonalInches: bestMatch.diagonalInches, cssPPI: cssDiag / bestMatch.diagonalInches };
    }
  }

  return null;
}

/**
 * Auto-detect monitor using the Window Management API (Chrome 100+).
 * Returns null if the API is unavailable or no match found.
 */
export async function autoDetectMonitor(): Promise<{
  specs: MonitorSpecs | MacBookModel;
  source: 'monitor-db';
  diagonalInches: number;
  ppi: number;
} | null> {
  try {
    // Check if the API is available
    if (!('getScreenDetails' in window)) return null;

    const screenDetails = await (window as any).getScreenDetails();
    if (!screenDetails?.currentScreen?.label) return null;

    const label: string = screenDetails.currentScreen.label;

    // Special handling for MacBook built-in displays
    if (/built.in|color lcd/i.test(label)) {
      const sw = window.screen.width;
      const sh = window.screen.height;
      for (const mb of macBookModels) {
        if (sw === mb.cssWidth && sh === mb.cssHeight) {
          const diag = Math.sqrt(sw * sw + sh * sh);
          const ppi = diag / mb.diagonalInches;
          return { specs: mb, source: 'monitor-db', diagonalInches: mb.diagonalInches, ppi };
        }
      }
      return null;
    }

    // Try matching against monitor database
    for (const monitor of knownMonitors) {
      if (monitor.pattern.test(label) && monitor.diagonalInches > 0) {
        const sw = window.screen.width;
        const sh = window.screen.height;
        const diag = Math.sqrt(sw * sw + sh * sh);
        const ppi = diag / monitor.diagonalInches;
        return { specs: monitor, source: 'monitor-db', diagonalInches: monitor.diagonalInches, ppi };
      }
    }

    // Fallback: try to extract screen size from label (e.g., "SomeMonitor 27Q" → 27")
    const sizeMatch = label.match(/\b(2[0-9]|3[0-9]|4[0-9])\b/);
    if (sizeMatch) {
      const size = parseInt(sizeMatch[1], 10);
      if (size >= 20 && size <= 49) {
        const sw = window.screen.width;
        const sh = window.screen.height;
        const diag = Math.sqrt(sw * sw + sh * sh);
        const ppi = diag / size;
        return {
          specs: {
            pattern: new RegExp(label, 'i'),
            diagonalInches: size,
            physicalWidthMM: 0,
            physicalHeightMM: 0,
            name: label,
          },
          source: 'monitor-db',
          diagonalInches: size,
          ppi,
        };
      }
    }

    return null;
  } catch {
    // Permission denied or API not available
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════
// Reference objects for calibration
// ═══════════════════════════════════════════════════════════════

export interface ReferenceObject {
  id: string;
  type: 'rect' | 'circle';
  widthMM: number;     // for rect
  heightMM: number;    // for rect
  diameterMM: number;  // for circle
  labelEN: string;
  labelZH: string;
  labelNL: string;
}

export const referenceObjects: ReferenceObject[] = [
  // Cards
  {
    id: 'credit-card',
    type: 'rect',
    widthMM: 85.60,
    heightMM: 53.98,
    diameterMM: 0,
    labelEN: 'Credit / Bank Card',
    labelZH: '银行卡 / 信用卡',
    labelNL: 'Bank- / Creditcard',
  },
  {
    id: 'id-card-cn',
    type: 'rect',
    widthMM: 85.60,
    heightMM: 54.00,
    diameterMM: 0,
    labelEN: 'ID Card (China)',
    labelZH: '身份证',
    labelNL: 'Identiteitskaart (China)',
  },
  // Coins
  {
    id: 'coin-cny-1yuan',
    type: 'circle',
    widthMM: 25,
    heightMM: 25,
    diameterMM: 25,
    labelEN: '1 Yuan Coin (¥1)',
    labelZH: '1元硬币',
    labelNL: '1 Yuan munt',
  },
  {
    id: 'coin-cny-5jiao',
    type: 'circle',
    widthMM: 20.5,
    heightMM: 20.5,
    diameterMM: 20.5,
    labelEN: '5 Jiao Coin (¥0.5)',
    labelZH: '5角硬币',
    labelNL: '5 Jiao munt',
  },
  {
    id: 'coin-cny-1jiao',
    type: 'circle',
    widthMM: 19,
    heightMM: 19,
    diameterMM: 19,
    labelEN: '1 Jiao Coin (¥0.1)',
    labelZH: '1角硬币',
    labelNL: '1 Jiao munt',
  },
  {
    id: 'coin-usd-quarter',
    type: 'circle',
    widthMM: 24.26,
    heightMM: 24.26,
    diameterMM: 24.26,
    labelEN: 'US Quarter ($0.25)',
    labelZH: '美国25美分硬币',
    labelNL: 'US Quarter ($0.25)',
  },
  {
    id: 'coin-eur-1',
    type: 'circle',
    widthMM: 23.25,
    heightMM: 23.25,
    diameterMM: 23.25,
    labelEN: '1 Euro Coin (€1)',
    labelZH: '1欧元硬币',
    labelNL: '1 Euro munt (€1)',
  },
  {
    id: 'coin-eur-2',
    type: 'circle',
    widthMM: 25.75,
    heightMM: 25.75,
    diameterMM: 25.75,
    labelEN: '2 Euro Coin (€2)',
    labelZH: '2欧元硬币',
    labelNL: '2 Euro munt (€2)',
  },
  // Paper
  {
    id: 'a4-short',
    type: 'rect',
    widthMM: 210,
    heightMM: 20,
    diameterMM: 0,
    labelEN: 'A4 Paper Short Edge (210mm)',
    labelZH: 'A4纸短边 (210mm)',
    labelNL: 'A4 papier korte zijde (210mm)',
  },
];

// ═══════════════════════════════════════════════════════════════
// Phone-as-reference database (for desktop calibration)
// ═══════════════════════════════════════════════════════════════

/**
 * Users can place their phone on the monitor screen to calibrate.
 * We know the phone's physical dimensions, so it works as a reference object.
 * No ruler needed — everyone has a phone!
 */
export interface PhoneReference {
  id: string;
  brand: string;
  model: string;
  widthMM: number;   // Physical body width
  heightMM: number;  // Physical body height
}

export const phoneReferences: PhoneReference[] = [
  // ── Apple iPhone ──
  { id: 'iphone-16-pro-max', brand: 'Apple', model: 'iPhone 16 Pro Max', widthMM: 77.6, heightMM: 163.0 },
  { id: 'iphone-16-pro', brand: 'Apple', model: 'iPhone 16 Pro', widthMM: 71.5, heightMM: 149.6 },
  { id: 'iphone-16', brand: 'Apple', model: 'iPhone 16', widthMM: 71.6, heightMM: 147.6 },
  { id: 'iphone-15-pro-max', brand: 'Apple', model: 'iPhone 15 Pro Max', widthMM: 76.7, heightMM: 159.9 },
  { id: 'iphone-15-pro', brand: 'Apple', model: 'iPhone 15 Pro', widthMM: 70.6, heightMM: 146.6 },
  { id: 'iphone-15', brand: 'Apple', model: 'iPhone 15', widthMM: 71.6, heightMM: 147.6 },
  { id: 'iphone-14-pro-max', brand: 'Apple', model: 'iPhone 14 Pro Max', widthMM: 77.6, heightMM: 160.7 },
  { id: 'iphone-14', brand: 'Apple', model: 'iPhone 14', widthMM: 71.5, heightMM: 146.7 },
  { id: 'iphone-13', brand: 'Apple', model: 'iPhone 13 / 14', widthMM: 71.5, heightMM: 146.7 },
  { id: 'iphone-se3', brand: 'Apple', model: 'iPhone SE (2/3)', widthMM: 67.3, heightMM: 138.4 },

  // ── Samsung Galaxy S ──
  { id: 'galaxy-s25-ultra', brand: 'Samsung', model: 'Galaxy S25 Ultra', widthMM: 77.6, heightMM: 162.8 },
  { id: 'galaxy-s25-plus', brand: 'Samsung', model: 'Galaxy S25+', widthMM: 75.8, heightMM: 158.4 },
  { id: 'galaxy-s25', brand: 'Samsung', model: 'Galaxy S25', widthMM: 70.5, heightMM: 146.9 },
  { id: 'galaxy-s24-ultra', brand: 'Samsung', model: 'Galaxy S24 Ultra', widthMM: 79.0, heightMM: 162.3 },
  { id: 'galaxy-s24', brand: 'Samsung', model: 'Galaxy S24', widthMM: 70.6, heightMM: 147.0 },
  { id: 'galaxy-s23-ultra', brand: 'Samsung', model: 'Galaxy S23 Ultra', widthMM: 78.1, heightMM: 163.4 },

  // ── Xiaomi ──
  { id: 'xiaomi-15-pro', brand: 'Xiaomi', model: 'Xiaomi 15 Pro', widthMM: 75.3, heightMM: 160.8 },
  { id: 'xiaomi-15', brand: 'Xiaomi', model: 'Xiaomi 15', widthMM: 71.2, heightMM: 152.3 },
  { id: 'xiaomi-14-ultra', brand: 'Xiaomi', model: 'Xiaomi 14 Ultra', widthMM: 75.3, heightMM: 161.4 },
  { id: 'xiaomi-14', brand: 'Xiaomi', model: 'Xiaomi 14', widthMM: 71.5, heightMM: 152.8 },
  { id: 'redmi-note-13-pro', brand: 'Xiaomi', model: 'Redmi Note 13 Pro+', widthMM: 74.2, heightMM: 161.4 },

  // ── Huawei ──
  { id: 'huawei-mate70-pro', brand: 'Huawei', model: 'Mate 70 Pro', widthMM: 74.6, heightMM: 162.8 },
  { id: 'huawei-mate60-pro', brand: 'Huawei', model: 'Mate 60 Pro', widthMM: 74.6, heightMM: 163.2 },
  { id: 'huawei-p60-pro', brand: 'Huawei', model: 'P60 Pro', widthMM: 74.5, heightMM: 161.2 },

  // ── Google Pixel ──
  { id: 'pixel-9-pro', brand: 'Google', model: 'Pixel 9 Pro', widthMM: 70.6, heightMM: 152.8 },
  { id: 'pixel-9', brand: 'Google', model: 'Pixel 9', widthMM: 68.7, heightMM: 152.4 },
  { id: 'pixel-8-pro', brand: 'Google', model: 'Pixel 8 Pro', widthMM: 76.5, heightMM: 162.6 },
  { id: 'pixel-8', brand: 'Google', model: 'Pixel 8', widthMM: 70.8, heightMM: 150.5 },

  // ── OnePlus / OPPO ──
  { id: 'oneplus-12', brand: 'OnePlus', model: 'OnePlus 12', widthMM: 75.8, heightMM: 164.3 },
  { id: 'oppo-find-x7-ultra', brand: 'OPPO', model: 'Find X7 Ultra', widthMM: 76.2, heightMM: 164.8 },

  // ── vivo ──
  { id: 'vivo-x100-pro', brand: 'vivo', model: 'X100 Pro', widthMM: 75.3, heightMM: 164.1 },
];

/**
 * Get unique brands from phone references.
 */
export function getPhoneBrands(): string[] {
  const brands = new Set(phoneReferences.map(p => p.brand));
  return Array.from(brands);
}

/**
 * Get phone models for a specific brand.
 */
export function getPhonesByBrand(brand: string): PhoneReference[] {
  return phoneReferences.filter(p => p.brand === brand);
}

/**
 * Compute PPI given a phone's known width in mm and its measured width in CSS pixels on screen.
 */
export function computePPIFromPhoneWidth(measuredWidthPx: number, phoneWidthMM: number): number {
  const widthInches = phoneWidthMM / 25.4;
  return measuredWidthPx / widthInches;
}

/**
 * Common screen sizes for quick selection
 */
export interface CommonScreenSize {
  diagonal: number;
  label: string;
  hint: string;        // device hint for user (e.g., "MacBook Pro")
  hintZH: string;      // Chinese hint
  category: 'laptop' | 'desktop';
}

export const commonScreenSizes: CommonScreenSize[] = [
  // Laptops
  { diagonal: 12.4, label: '12.4"', hint: 'Surface Pro', hintZH: 'Surface Pro', category: 'laptop' },
  { diagonal: 13.3, label: '13.3"', hint: 'MacBook Air (old) / ThinkPad', hintZH: '旧款MacBook Air / ThinkPad', category: 'laptop' },
  { diagonal: 13.4, label: '13.4"', hint: 'Dell XPS 13', hintZH: 'Dell XPS 13', category: 'laptop' },
  { diagonal: 13.6, label: '13.6"', hint: 'MacBook Air M2/M3', hintZH: 'MacBook Air M2/M3', category: 'laptop' },
  { diagonal: 14, label: '14"', hint: 'ThinkPad / HP / Common', hintZH: 'ThinkPad / HP / 常见笔记本', category: 'laptop' },
  { diagonal: 14.2, label: '14.2"', hint: 'MacBook Pro 14"', hintZH: 'MacBook Pro 14寸', category: 'laptop' },
  { diagonal: 15.3, label: '15.3"', hint: 'MacBook Air 15"', hintZH: 'MacBook Air 15寸', category: 'laptop' },
  { diagonal: 15.6, label: '15.6"', hint: 'Common laptop', hintZH: '常见笔记本', category: 'laptop' },
  { diagonal: 16, label: '16"', hint: 'HP / Dell / Lenovo 16"', hintZH: 'HP / Dell / 联想 16寸', category: 'laptop' },
  { diagonal: 16.2, label: '16.2"', hint: 'MacBook Pro 16"', hintZH: 'MacBook Pro 16寸', category: 'laptop' },
  { diagonal: 17.3, label: '17.3"', hint: 'Large gaming laptop', hintZH: '大型游戏本', category: 'laptop' },
  // Desktop monitors
  { diagonal: 21.5, label: '21.5"', hint: 'iMac 21.5" / Small monitor', hintZH: 'iMac 21.5寸 / 小型显示器', category: 'desktop' },
  { diagonal: 23.8, label: '23.8" / 24"', hint: 'iMac 24" / Common monitor', hintZH: 'iMac 24寸 / 常见显示器', category: 'desktop' },
  { diagonal: 27, label: '27"', hint: 'iMac 27" / Dell / LG 27"', hintZH: 'iMac 27寸 / Dell / LG 27寸', category: 'desktop' },
  { diagonal: 32, label: '32"', hint: 'Studio Display / 4K 32"', hintZH: 'Studio Display / 4K 32寸', category: 'desktop' },
  { diagonal: 34, label: '34" UW', hint: 'Ultrawide 21:9', hintZH: '超宽屏 21:9', category: 'desktop' },
  { diagonal: 38, label: '38" UW', hint: 'LG UltraWide 38"', hintZH: 'LG 超宽屏 38寸', category: 'desktop' },
  { diagonal: 43, label: '43"', hint: 'Large 4K monitor', hintZH: '大型4K显示器', category: 'desktop' },
  { diagonal: 49, label: '49" SUW', hint: 'Samsung / LG Super UW', hintZH: '三星 / LG 超超宽', category: 'desktop' },
];

// ═══════════════════════════════════════════════════════════════
// Core PPI calculations
// ═══════════════════════════════════════════════════════════════

/**
 * Estimate the base DPR (DPR at 100% browser zoom) for zoom compensation.
 */
export function estimateBaseDPR(): number {
  const dpr = window.devicePixelRatio;
  const ua = navigator.userAgent.toLowerCase();
  const platform = (navigator as any).platform?.toLowerCase() || '';

  if (platform.includes('mac') || ua.includes('macintosh')) {
    return dpr >= 1.5 ? 2 : 1;
  }
  if (/iphone|ipad|ipod/.test(ua)) {
    return dpr >= 2.5 ? 3 : 2;
  }
  if (/android/.test(ua)) {
    return Math.round(dpr * 2) / 2;
  }
  if (dpr > 1.3 && dpr < 1.7) return 1.5;
  if (dpr >= 1.7) return 2;
  return 1;
}

/**
 * Calculate CSS pixels per physical inch, given screen diagonal in inches.
 */
export function calculateCssPixelsPerInch(diagonalInches: number): number {
  const sw = window.screen.width;
  const sh = window.screen.height;
  const diagonalCssPx = Math.sqrt(sw * sw + sh * sh);
  return diagonalCssPx / diagonalInches;
}

/**
 * Calculate CSS pixels per physical inch WITH zoom compensation.
 */
export function calculateAdjustedPxPerInch(
  diagonalInches: number,
  baseDPR: number
): number {
  const basePxPerInch = calculateCssPixelsPerInch(diagonalInches);
  const zoomFactor = window.devicePixelRatio / baseDPR;
  return basePxPerInch / zoomFactor;
}

/**
 * Check if the current device is likely mobile.
 */
export function isMobileDevice(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)
    || window.innerWidth <= 768;
}

/**
 * Check if the current device is likely a tablet.
 */
export function isTabletDevice(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /ipad|tablet/i.test(ua)
    || (window.innerWidth > 768 && window.innerWidth <= 1024);
}

// ═══════════════════════════════════════════════════════════════
// Calibration persistence
// ═══════════════════════════════════════════════════════════════

// ISO/IEC 7810 ID-1 standard credit card dimensions
export const CREDIT_CARD_WIDTH_MM = 85.60;
export const CREDIT_CARD_HEIGHT_MM = 53.98;
export const CREDIT_CARD_WIDTH_INCH = CREDIT_CARD_WIDTH_MM / 25.4;
export const CREDIT_CARD_HEIGHT_INCH = CREDIT_CARD_HEIGHT_MM / 25.4;

const CALIBRATION_KEY = 'ruler_calibrated_px_per_inch';
const CALIBRATION_DPR_KEY = 'ruler_calibrated_dpr';
const CALIBRATION_SOURCE_KEY = 'ruler_calibration_source';

/**
 * Save a calibrated PPI value with source tracking.
 */
export function saveCalibration(cssPixelsPerInch: number, source: CalibrationSource = 'calibration'): void {
  localStorage.setItem(CALIBRATION_KEY, String(cssPixelsPerInch));
  localStorage.setItem(CALIBRATION_DPR_KEY, String(window.devicePixelRatio));
  localStorage.setItem(CALIBRATION_SOURCE_KEY, source);
}

/**
 * Load calibrated PPI from storage.
 */
export function loadCalibration(): {
  pxPerInch: number;
  calibratedDPR: number;
  source: CalibrationSource;
} | null {
  const saved = localStorage.getItem(CALIBRATION_KEY);
  const savedDPR = localStorage.getItem(CALIBRATION_DPR_KEY);
  if (!saved || !savedDPR) return null;
  const source = (localStorage.getItem(CALIBRATION_SOURCE_KEY) as CalibrationSource) || 'calibration';
  return { pxPerInch: parseFloat(saved), calibratedDPR: parseFloat(savedDPR), source };
}

/**
 * Clear stored calibration.
 */
export function clearCalibration(): void {
  localStorage.removeItem(CALIBRATION_KEY);
  localStorage.removeItem(CALIBRATION_DPR_KEY);
  localStorage.removeItem(CALIBRATION_SOURCE_KEY);
}

/**
 * Get the effective CSS-pixels-per-inch, accounting for zoom.
 */
export function getCalibratedPxPerInch(): number | null {
  const cal = loadCalibration();
  if (!cal) return null;
  const zoomFactor = window.devicePixelRatio / cal.calibratedDPR;
  return cal.pxPerInch / zoomFactor;
}

/**
 * Given the width of a reference object in CSS pixels and its known width in mm,
 * compute the CSS-pixels-per-inch at the current DPR.
 */
export function computePPIFromWidthMM(cssPixels: number, realWidthMM: number): number {
  const realWidthInch = realWidthMM / 25.4;
  return cssPixels / realWidthInch;
}

/**
 * Given the width of the credit card rectangle in CSS pixels,
 * compute the CSS-pixels-per-inch at the current DPR.
 */
export function computePPIFromCardWidth(cardWidthCSSPx: number): number {
  return cardWidthCSSPx / CREDIT_CARD_WIDTH_INCH;
}

/**
 * Given a diameter in CSS pixels and real diameter in mm (for coins),
 * compute the CSS-pixels-per-inch at the current DPR.
 */
export function computePPIFromDiameter(cssPixelDiameter: number, realDiameterMM: number): number {
  const realDiameterInch = realDiameterMM / 25.4;
  return cssPixelDiameter / realDiameterInch;
}
